import { useQuery } from '@tanstack/react-query';

import { ADMIN_LIST } from '~features/admin/Preferences/constant';
import { fetcher } from '~lib/api/fetch';
import { queryClient } from '~lib/api/queryClient';
import useAdminStore from '~store/admin';
import { IUserInfo } from '~types/user';
import getTokenFromCookie from '~utils/auth/getTokenFromCookie';
import resetTokenToCookie from '~utils/auth/resetTokenToCookie';
import { setAmplitudeUserProperties } from '~utils/common/thirdPartyEvent';

import QueryKeys from '../queryKeys';
import { useOnboardingTagsQuery } from './useOnboardingTagsQuery';

interface Params {
  onSuccess?: (response: IUserInfo) => void;
  onError?: (response: any) => void;
  refetchOnMount?: boolean;
  notReloadOnInvalidToken?: boolean;
}

export default function useGetMyInfoQuery(params?: Params) {
  const { setIsAdmin } = useAdminStore();
  const { accessToken } = getTokenFromCookie();
  const isFindedMyInfoQuery = queryClient.getQueryData([QueryKeys.myInfo]);
  const { data: tags } = useOnboardingTagsQuery();
  const query = useQuery<IUserInfo>(
    [QueryKeys.myInfo],
    () => fetcher('/api/user/userinfo', { data: params?.notReloadOnInvalidToken }),
    {
      refetchOnMount: params?.refetchOnMount,
      enabled: !!accessToken,
      onSuccess: (myInfoData) => {
        if (!isFindedMyInfoQuery) {
          setAmplitudeUserProperties({
            profile_level: Number(myInfoData?.level),
            login: true,
            annual: myInfoData?.career,
            salary: myInfoData.salaries?.current,
            jobCategory: myInfoData?.job?.jobGroup ? myInfoData?.job?.jobGroup.title : null,
            jobRole: myInfoData?.job?.jobs
              ? myInfoData?.job?.jobs.map((item) => item.title).join()
              : null,
            accept_event_email: myInfoData?.marketingAgreements.email,
            accept_event_message: myInfoData?.marketingAgreements.sms,
            is_clean_review: myInfoData.workLifeReview,
            career_interest: myInfoData?.tagIds
              ?.map((careerTagId) => {
                const career = tags?.career.find((item) => item.id === careerTagId);

                return career?.name;
              })
              .filter((value) => !!value)
              .join(),
            company_interest: myInfoData?.tagIds
              ?.map((companyTagId) => {
                const company = tags?.company.find((item) => item.id === companyTagId);

                return company?.name;
              })
              .filter((value) => !!value)
              .join(),
          });
          setIsAdmin(ADMIN_LIST.includes(myInfoData.email));
        }
      },
      onError: (e) => {
        resetTokenToCookie();
        if (params?.onError) {
          params.onError(e);

          return;
        }
      },
    },
  );

  return { ...query, isActiveUser: !!accessToken || query.isSuccess };
}
